import { useMutation } from '@apollo/client';
import { Button, ModalContent } from '@sandtech-hub-monorepo/ui-kit';
import { BulbIcon } from '@sandtech-hub-monorepo/ui-kit-site';
import { ReactNode } from 'react';
import { VerificationModalHeader } from './VerificationModalHeader';
import {
  GetViewerProgramEnrollmentsWithApplicationDocument,
  ManagePersonaInquiryDocument,
  PersonaInquiryAction,
  PersonaInquiryType,
} from '@sandtech-hub-monorepo/ehub-client';

interface PersonaVerificationProps {
  icon: ReactNode;
  title: string;
  description: string;
  programApplicationId: string;
  inquiryType: PersonaInquiryType;
  close: () => void;
  fallbackAction?: {
    text: string;
    onClick: () => void;
  };
}

export const PersonaVerification = ({
  icon,
  title,
  description,
  programApplicationId,
  inquiryType,
  close,
  fallbackAction,
}: PersonaVerificationProps) => {
  const [managePersonaInquiry, { loading }] = useMutation(
    ManagePersonaInquiryDocument,
    {
      onCompleted: (data) => {
        if (data.managePersonaInquiry.redirectUrl) {
          window.open(data.managePersonaInquiry.redirectUrl, '_blank');
          close();
        }
      },
      onError: (error) => {
        console.error('Failed to manage Persona inquiry:', error);
      },
      refetchQueries: [GetViewerProgramEnrollmentsWithApplicationDocument],
    }
  );

  return (
    <ModalContent>
      <VerificationModalHeader
        icon={icon}
        title={title}
        description={description}
      />

      <div className="flex flex-col gap-6 max-w-screen-xs mx-auto">
        <div className="bg-background-quaternary p-6 rounded-lg flex gap-6 items-center mt-8">
          <div className="bg-background rounded-full p-3 text-primary shrink-0">
            <BulbIcon />
          </div>
          <div className="text-foreground text-sm text-left">
            This will open a secure page on our partner service{' '}
            <strong>Persona</strong>. You'll be redirected outside this app to
            complete this step.
          </div>
        </div>
      </div>

      <div className="mx-auto my-8">
        <Button
          onClick={() =>
            managePersonaInquiry({
              variables: {
                input: {
                  programApplicationId,
                  inquiryType,
                  action: PersonaInquiryAction.Generate,
                },
              },
            })
          }
          disabled={loading}
        >
          {loading
            ? 'Opening verification page...'
            : 'Take me to verification page'}
        </Button>
      </div>

      {fallbackAction && (
        <div className="text-center text-foreground max-w-screen-xs mx-auto">
          {fallbackAction.text}{' '}
          <button
            className="font-semibold text-primary underline-offset-4 hover:underline"
            onClick={fallbackAction.onClick}
          >
            click here
          </button>
          .
        </div>
      )}
    </ModalContent>
  );
};
